import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { login, requestPasswordReset, signup, verifySignupOtp, verifyPasswordResetOtp, resetPasswordWithToken } from "@/lib/api/auth";
import { useApp } from "@/contexts/AppContext";
import { StoreLayout } from "@/components/layout/StoreLayout";

type Tab = "login" | "signup" | "forgot" | "forgot-otp" | "forgot-new";

const TABS: Tab[] = ["login", "signup", "forgot"];

/* ─── Shared style tokens ─── */
const inputCls =
  "w-full px-4 py-3.5 rounded-xl border text-body-md outline-none transition-all duration-300 " +
  "bg-surface-container-lowest border-outline-variant/50 text-on-surface placeholder:text-outline-variant/60 " +
  "focus:border-golden-latte focus:bg-surface-container-lowest focus:shadow-[0_0_0_3px_color-mix(in_oklab,var(--color-golden-latte)_15%,transparent)]";
const labelCls = "block text-[10px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant mb-1.5";
const btnCls =
  "relative w-full py-3.5 rounded-xl text-label-md font-label-md uppercase tracking-[0.16em] transition-all duration-300 " +
  "bg-primary text-white " +
  "hover:shadow-[0_8px_32px_-8px_color-mix(in_oklab,var(--color-primary)_60%,transparent)] hover:scale-[1.01] active:scale-[0.99] " +
  "disabled:opacity-60 disabled:hover:scale-100 disabled:hover:shadow-none";
const ghostBtnCls =
  "w-full py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-on-surface-variant " +
  "hover:text-primary transition-colors duration-200";

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className={labelCls} htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

function Panel({ id, dir, children }: { id: string; dir: number; children: React.ReactNode }) {
  return (
    <motion.div
      key={id}
      initial={{ opacity: 0, x: dir * 30, filter: "blur(8px)" }}
      animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, x: dir * -30, filter: "blur(8px)" }}
      transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function AuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loadingAuth, refreshAuth } = useApp();
  const [tab, setTab] = useState<Tab>("login");
  const dir = useRef(1);
  const [otpEmail, setOtpEmail] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ firstName: "", lastName: "", email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (!loadingAuth && user) {
      if (user.role === "admin" || user.role === "super_admin") navigate("/admin", { replace: true });
      else {
        const requestedPath = new URLSearchParams(location.search).get("redirect");
        const destination = requestedPath?.startsWith("/") && !requestedPath.startsWith("//")
          ? requestedPath
          : "/profile";
        navigate(destination, { replace: true });
      }
    }
  }, [user, loadingAuth, navigate, location.search]);

  const go = (next: Tab) => {
    dir.current = TABS.indexOf(next) >= TABS.indexOf(tab) ? 1 : -1;
    setTab(next);
  };

  const tabKey = tab === "signup" && otpEmail ? "otp" : tab;

  async function doLogin(e: React.FormEvent) {
    e.preventDefault();
    setPending("login");
    try {
      const u = await login(loginForm);
      await refreshAuth();
      toast.success(t("auth.welcomeToast", { name: u.firstName }));
    } catch (err: any) { toast.error(err.message); }
    finally { setPending(null); }
  }

  async function doSignup(e: React.FormEvent) {
    e.preventDefault();
    setPending("signup");
    try {
      const r = await signup(signupForm);
      if (r.otpRequired) { setOtpEmail(r.email); toast.success(t("auth.otpSent", { email: r.email })); return; }
      await refreshAuth();
      toast.success(t("auth.accountReady"));
    } catch (err: any) { toast.error(err.message); }
    finally { setPending(null); }
  }

  async function doVerify(e: React.FormEvent) {
    e.preventDefault();
    setPending("verify");
    try {
      await verifySignupOtp({ email: otpEmail ?? signupForm.email, code: otpCode });
      await refreshAuth();
      toast.success(t("auth.accountReady"));
    } catch (err: any) { toast.error(err.message); }
    finally { setPending(null); }
  }

  async function doReset(e: React.FormEvent) {
    e.preventDefault();
    setPending("reset");
    try {
      await requestPasswordReset(resetEmail);
      toast.success(t("auth.otpSent", { email: resetEmail }));
      setResendTimer(10);
      go("forgot-otp");
    } catch (err: any) { toast.error(err.message); }
    finally { setPending(null); }
  }

  async function doResendResetOtp() {
    setPending("resend-reset");
    try {
      await requestPasswordReset(resetEmail);
      toast.success(t("auth.otpSent", { email: resetEmail }));
      setResendTimer(10);
    } catch (err: any) { toast.error(err.message); }
    finally { setPending(null); }
  }

  async function doVerifyResetOtp(e: React.FormEvent) {
    e.preventDefault();
    setPending("verify-reset");
    try {
      const { resetToken } = await verifyPasswordResetOtp({ email: resetEmail, code: resetOtp });
      setResetToken(resetToken);
      go("forgot-new");
    } catch (err: any) { toast.error(err.message); }
    finally { setPending(null); }
  }

  async function doResetSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPending("reset-submit");
    try {
      await resetPasswordWithToken({ token: resetToken, password: newPassword });
      toast.success(t("auth.passwordChanged") || "Password changed successfully!");
      go("login");
    } catch (err: any) { toast.error(err.message); }
    finally { setPending(null); }
  }

  return (
    <StoreLayout showAnnouncement={true} showFooter={true} className="flex flex-col relative bg-surface-container-low min-h-[80vh]">
      <main className="flex-grow flex items-center justify-center px-4 py-16 relative overflow-hidden">
        
        {/* Subtle background element */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
           <div className="absolute top-0 right-0 w-[50vw] h-[50vw] rounded-full bg-golden-latte/10 blur-[100px] -translate-y-1/2 translate-x-1/4" />
           <div className="absolute bottom-0 left-0 w-[40vw] h-[40vw] rounded-full bg-primary/5 blur-[100px] translate-y-1/3 -translate-x-1/3" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-full max-w-[440px]"
        >
          {/* Card */}
          <div className="rounded-2xl overflow-hidden bg-surface-container-lowest luxury-shadow border border-outline-variant/30">
            {/* Header */}
            <div className="px-8 pt-10 pb-0">
              <div className="text-center mb-8">
                <h1 className="text-headline-sm font-headline-sm text-primary mb-2">SEEN</h1>
                <p className="text-label-sm font-label-sm text-on-surface-variant uppercase tracking-widest">{t("auth.joinTitle")}</p>
              </div>

              {/* Tab switcher */}
              <div className="flex gap-1 mb-0 p-1 rounded-xl bg-surface-container-low border border-outline-variant/30">
                {(["login", "signup"] as Tab[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => go(key)}
                    className={`relative flex-1 py-2.5 rounded-lg text-[11px] font-semibold uppercase tracking-[0.14em] transition-all duration-200 focus:outline-none ${tab === key || (tab === "forgot" && key === "login") ? "text-primary" : "text-on-surface-variant hover:text-primary/70"}`}
                  >
                    {(tab === key || (tab === "forgot" && key === "login")) && (
                      <motion.span
                        layoutId="auth-pill"
                        className="absolute inset-0 rounded-lg bg-surface-container-lowest shadow-sm"
                        transition={{ type: "spring", stiffness: 500, damping: 38 }}
                      />
                    )}
                    <span className="relative z-10">{t(`auth.${key}`)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Form area */}
            <div className="px-8 pt-8 pb-10">
              <AnimatePresence mode="wait" initial={false}>
                {/* ── LOGIN ── */}
                {tabKey === "login" && (
                  <Panel id="login" dir={dir.current}>
                    <form onSubmit={doLogin} className="space-y-5">
                      <div>
                        <h2 className="text-title-lg font-title-lg text-primary">{t("auth.welcome")}</h2>
                        <p className="text-body-sm text-on-surface-variant mt-1">{t("auth.welcomeDesc")}</p>
                      </div>
                      <div className="space-y-4">
                        <Field label={t("auth.email")} id="l-email">
                          <input id="l-email" type="email" autoComplete="email" placeholder={t("auth.placeholders.email")}
                            className={inputCls} value={loginForm.email}
                            onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} />
                        </Field>
                        <Field label={t("auth.password")} id="l-pw">
                          <input id="l-pw" type="password" autoComplete="current-password" placeholder={t("auth.placeholders.password")}
                            className={inputCls} value={loginForm.password}
                            onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} />
                        </Field>
                      </div>
                      <div className="flex justify-end">
                        <button type="button" onClick={() => go("forgot")}
                          className="text-[11px] text-primary/70 hover:text-primary transition-colors tracking-wide">
                          {t("auth.forgot")} {t("auth.password")}?
                        </button>
                      </div>
                      <button type="submit" disabled={pending === "login"} className={btnCls}>
                        {pending === "login" ? t("auth.signingIn") : t("auth.signIn")}
                      </button>
                      <p className="text-center text-[11px] text-on-surface-variant">
                        {t("auth.noAccount")}{" "}
                        <button type="button" onClick={() => go("signup")}
                          className="text-primary hover:text-golden-latte transition-colors font-semibold">
                          {t("auth.registerNow")}
                        </button>
                      </p>
                    </form>
                  </Panel>
                )}

                {/* ── SIGN UP ── */}
                {tabKey === "signup" && (
                  <Panel id="signup" dir={dir.current}>
                    <form onSubmit={doSignup} className="space-y-5">
                      <div>
                        <h2 className="text-title-lg font-title-lg text-primary">{t("auth.joinTitle")}</h2>
                        <p className="text-body-sm text-on-surface-variant mt-1">{t("auth.joinDesc")}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label={t("auth.firstName")} id="s-fn">
                          <input id="s-fn" type="text" placeholder={t("auth.placeholders.firstName")}
                            className={inputCls} value={signupForm.firstName}
                            onChange={e => setSignupForm(p => ({ ...p, firstName: e.target.value }))} />
                        </Field>
                        <Field label={t("auth.lastName")} id="s-ln">
                          <input id="s-ln" type="text" placeholder={t("auth.placeholders.lastName")}
                            className={inputCls} value={signupForm.lastName}
                            onChange={e => setSignupForm(p => ({ ...p, lastName: e.target.value }))} />
                        </Field>
                      </div>
                      <Field label={t("auth.email")} id="s-email">
                        <input id="s-email" type="email" autoComplete="email" placeholder={t("auth.placeholders.signupEmail")}
                          className={inputCls} value={signupForm.email}
                          onChange={e => setSignupForm(p => ({ ...p, email: e.target.value }))} />
                      </Field>
                      <Field label={t("auth.password")} id="s-pw">
                        <input id="s-pw" type="password" autoComplete="new-password" placeholder={t("auth.placeholders.createPassword")}
                          className={inputCls} value={signupForm.password}
                          onChange={e => setSignupForm(p => ({ ...p, password: e.target.value }))} />
                      </Field>
                      <button type="submit" disabled={pending === "signup"} className={btnCls}>
                        {pending === "signup" ? t("auth.creating") : t("auth.createAccount")}
                      </button>
                      <p className="text-center text-[10px] text-on-surface-variant/70 leading-relaxed max-w-[280px] mx-auto">
                        {t("auth.legal")}{" "}
                        <a href="#" className="text-on-surface-variant hover:text-primary underline underline-offset-2">{t("auth.privacy")}</a>
                        {" "}{t("auth.and")}{" "}
                        <a href="#" className="text-on-surface-variant hover:text-primary underline underline-offset-2">{t("auth.terms")}</a>.
                      </p>
                    </form>
                  </Panel>
                )}

                {/* ── OTP ── */}
                {tabKey === "otp" && (
                  <Panel id="otp" dir={1}>
                    <form onSubmit={doVerify} className="space-y-5">
                      <div>
                        <h2 className="text-title-lg font-title-lg text-primary">{t("auth.otpTitle")}</h2>
                        <p className="text-body-sm text-on-surface-variant mt-1">{t("auth.otpDesc", { email: otpEmail })}</p>
                      </div>
                      <Field label={t("auth.otpCode")} id="otp-c">
                        <input id="otp-c" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                          placeholder="• • • • • •"
                          className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold py-6`}
                          value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, ""))} />
                      </Field>
                      <button type="submit" disabled={pending === "verify" || otpCode.length < 4} className={btnCls}>
                        {pending === "verify" ? t("auth.verifying") : t("auth.verify")}
                      </button>
                      <button type="button" onClick={doSignup as any} disabled={pending === "signup"} className={ghostBtnCls}>
                        {t("auth.otpResend")}
                      </button>
                    </form>
                  </Panel>
                )}

                {/* ── FORGOT PASSWORD ── */}
                {tabKey === "forgot" && (
                  <Panel id="forgot" dir={dir.current}>
                    <form onSubmit={doReset} className="space-y-5">
                      <div>
                        <h2 className="text-title-lg font-title-lg text-primary">{t("auth.resetTitle")}</h2>
                        <p className="text-body-sm text-on-surface-variant mt-1">{t("auth.resetDesc") || "Enter your email and we'll send a recovery OTP."}</p>
                      </div>
                      <Field label={t("auth.email")} id="r-email">
                        <input id="r-email" type="email" placeholder={t("auth.placeholders.email")}
                          className={inputCls} value={resetEmail}
                          onChange={e => setResetEmail(e.target.value)} />
                      </Field>
                      <div className="flex items-start gap-3 rounded-xl p-4 bg-surface-container-low border border-outline-variant/30">
                        <span className="text-golden-latte text-base shrink-0 mt-0.5">ℹ</span>
                        <p className="text-xs text-on-surface-variant leading-relaxed">{t("auth.spamHint")}</p>
                      </div>
                      <button type="submit" disabled={pending === "reset"} className={btnCls}>
                        {pending === "reset" ? t("auth.sending") : t("auth.sendOtp") || "Send OTP"}
                      </button>
                      <button type="button" onClick={() => go("login")} className={ghostBtnCls}>
                        ← {t("auth.backToLogin")}
                      </button>
                    </form>
                  </Panel>
                )}

                {/* ── FORGOT OTP ── */}
                {tabKey === "forgot-otp" && (
                  <Panel id="forgot-otp" dir={1}>
                    <form onSubmit={doVerifyResetOtp} className="space-y-5">
                      <div>
                        <h2 className="text-title-lg font-title-lg text-primary">{t("auth.otpTitle")}</h2>
                        <p className="text-body-sm text-on-surface-variant mt-1">{t("auth.otpDesc", { email: resetEmail })}</p>
                      </div>
                      <Field label={t("auth.otpCode")} id="rotp-c">
                        <input id="rotp-c" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                          placeholder="• • • • • •"
                          className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold py-6`}
                          value={resetOtp} onChange={e => setResetOtp(e.target.value.replace(/\D/g, ""))} />
                      </Field>
                      <button type="submit" disabled={pending === "verify-reset" || resetOtp.length < 4} className={btnCls}>
                        {pending === "verify-reset" ? t("auth.verifying") : t("auth.verify")}
                      </button>
                      <button
                        type="button"
                        onClick={doResendResetOtp}
                        disabled={resendTimer > 0 || pending === "resend-reset"}
                        className={ghostBtnCls}
                      >
                        {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
                      </button>
                      <button type="button" onClick={() => go("login")} className={ghostBtnCls}>
                        ← {t("auth.backToLogin")}
                      </button>
                    </form>
                  </Panel>
                )}

                {/* ── FORGOT NEW PASSWORD ── */}
                {tabKey === "forgot-new" && (
                  <Panel id="forgot-new" dir={1}>
                    <form onSubmit={doResetSubmit} className="space-y-5">
                      <div>
                        <h2 className="text-title-lg font-title-lg text-primary">{t("auth.newPasswordTitle") || "New Password"}</h2>
                        <p className="text-body-sm text-on-surface-variant mt-1">{t("auth.newPasswordDesc") || "Enter your new password."}</p>
                      </div>
                      <Field label={t("auth.password")} id="n-pw">
                        <input id="n-pw" type="password" autoComplete="new-password" placeholder={t("auth.placeholders.createPassword")}
                          className={inputCls} value={newPassword}
                          onChange={e => setNewPassword(e.target.value)} />
                      </Field>
                      <button type="submit" disabled={pending === "reset-submit"} className={btnCls}>
                        {pending === "reset-submit" ? t("auth.saving") || "Saving..." : t("auth.savePassword") || "Save Password"}
                      </button>
                      <button type="button" onClick={() => go("login")} className={ghostBtnCls}>
                        ← {t("auth.backToLogin")}
                      </button>
                    </form>
                  </Panel>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </main>
    </StoreLayout>
  );
}
